import { z } from 'zod'
import { resultEnvelope } from './envelope'
import type { ApiClient } from './client'

/**
 * Streams — GET /{clusterId}/mgmt/core/stream-management/
 *
 * Shape captured live from the gotham scope of the ap1 tenant on 2026-08-21
 * (53 streams), not guessed. What the capture established:
 *
 * - **This endpoint ignores pagination.** Asking for `pagesize=5` returned all
 *   53 rows and `count: 53`. So it is a fetch-everything endpoint, and paging
 *   or filtering has to happen client-side. That also means client-side counts
 *   here are accurate over the whole set, unlike device-management.
 * - `search_type` matters, but not the way the name suggests: `online` and
 *   `historic` returned BYTE-IDENTICAL payloads (0 differing rows, 0 differing
 *   fields across all 53). Omitting it entirely returned only 42 streams — 11
 *   fewer. So always send it; treat the online/historic distinction as
 *   unproven rather than assuming it filters anything.
 * - `running_workbooks` is the only field with real variation in this tenant:
 *   compression was 'High', retention 90 and replication 1 on every row. Those
 *   are genuine fields that happen to be uniform here, not constants.
 *
 * `evt_len` and `total_len` are BYTE counts, confirmed by the product owner.
 * The capture alone could not establish this — size_on_disk/total_len ranged
 * 0.08 to 9.9 across streams, so there was no ratio to infer a unit from — and
 * they were previously rendered as bare numbers rather than guessed at. They
 * are now formatted with formatBytes like size_on_disk.
 *
 * Note the three byte fields are NOT derivable from one another: disk can be
 * both larger and smaller than total_len depending on the stream. Do not
 * present any ratio between them as a compression figure.
 */

export const streamWorkbooks = z
  .object({
    total_workbook_count: z.number().default(0),
    inactive_total_workbook_count: z.number().default(0),
    scheduled: z
      .object({
        scheduled_workbook_count: z.number().default(0),
        status: z.boolean().default(false),
        ids: z.array(z.unknown()).default([]),
      })
      .partial()
      .passthrough()
      .default({}),
    streamed: z
      .object({
        streamed_workbook_count: z.number().default(0),
        status: z.boolean().default(false),
        ids: z.array(z.unknown()).default([]),
      })
      .partial()
      .passthrough()
      .default({}),
  })
  .passthrough()

export const stream = z
  .object({
    stream: z.string(),
    /** Bytes. */
    size_on_disk: z.number().default(0),
    /** Bytes. */
    evt_len: z.number().default(0),
    /** Bytes. */
    total_len: z.number().default(0),
    /** Days. */
    retention: z.number().nullable().default(null),
    replication: z.number().nullable().default(null),
    compression: z.string().nullable().default(null),
    running_workbooks: streamWorkbooks.default({}),
  })
  .passthrough()

export const streamsResponse = resultEnvelope(stream)

export type Stream = z.output<typeof stream>

/* ------------------------------------------------- detection coverage */

/**
 * POST /{clusterId}/wrk/api/stream/analytics — MITRE coverage per stream.
 *
 * A THIRD envelope shape: `{ result: { [streamName]: {...} }, status }` — no
 * `data`, no `count`. Do not reuse resultEnvelope here.
 *
 * Two traps, both observed live:
 * - The result carries a `*` key (a wildcard aggregate) that is not a stream.
 *   It is stripped, or it shows up as a phantom row.
 * - Only 42 of the tenant's 53 streams appear, and 11 keys name streams that
 *   stream-management does not return at all. So a stream missing from this map
 *   means COVERAGE UNKNOWN, which is not the same as zero techniques — the UI
 *   must be able to tell those apart.
 */
export const streamCoverage = z
  .object({
    tactics: z.number().default(0),
    techniques: z.number().default(0),
    workbooks: z.number().default(0),
    active_tactics: z.number().default(0),
    active_techniques: z.number().default(0),
    active_workbooks: z.number().default(0),
  })
  .passthrough()

export const streamAnalyticsResponse = z.object({
  result: z.record(streamCoverage).default({}),
  status: z.string().optional(),
})

export type StreamCoverage = z.output<typeof streamCoverage>

/* ------------------------------------------------------ daily volume */

/**
 * POST /{clusterId}/mgmt/core/daily-stream-details — EVENT COUNT per stream
 * so far today.
 *
 * The field is called `evt_len`, the same name stream-management uses for a
 * BYTE count, but it does not mean the same thing. Do not assume it does — an
 * earlier version of this client did, and rendered "922.7 MB/day" for what was
 * really 922.7 million events.
 *
 * How that was established, since the payload says nothing about units:
 * - Cumulative total_len over the 90-day retention implies ~480 GB/day of
 *   logical data fleet-wide, against a daily figure of ~1.1e9. If the daily
 *   figure were bytes it would be ~500x too small.
 * - Dividing implied bytes/day by the daily figure gives an average event size
 *   of 300-480 bytes per stream (median 446, fleet 427), which is exactly what
 *   log events look like. 46 of 53 streams land in a plausible range.
 *
 * It is also a RUNNING total for the current day, not a completed one: three
 * calls minutes apart returned 733M, 922M and 1,125M. Treat it as "today so
 * far", never as a full-day rate.
 *
 * Note `data` is a BARE ARRAY here, not `{ result: [] }` like the mgmt list
 * endpoints. Covered all 53 streams in the capture, none zero.
 */
export const dailyStreamResponse = z.object({
  count: z.number().default(0),
  message: z.string().optional(),
  status: z.string().optional(),
  TStamp: z.string().optional(),
  data: z
    .array(z.object({ stream: z.string(), evt_len: z.number().default(0) }).passthrough())
    .default([]),
})

/** The endpoint ignores pagination, so this is always the complete set. */
export interface StreamsResult {
  streams: Stream[]
  /** The server's own count. Equal to streams.length on this endpoint. */
  total: number
}

export interface StreamsQuery {
  clusterId: string
  scopeId: string
  /**
   * Always sent. Omitting it returns fewer streams (42 of 53 in the capture).
   * online and historic were identical, so this defaults to online.
   */
  searchType?: 'online' | 'historic'
}

export interface StreamsApi {
  list: (q: StreamsQuery) => Promise<StreamsResult>
  /** MITRE coverage keyed by stream name. Absent key = unknown, not zero. */
  coverage: (q: Omit<StreamsQuery, 'searchType'>) => Promise<Record<string, StreamCoverage>>
  /** Events received per stream SO FAR TODAY, keyed by stream name. Not bytes. */
  dailyEventCount: (q: Omit<StreamsQuery, 'searchType'>) => Promise<Record<string, number>>
}

export function createStreamsApi(client: ApiClient): StreamsApi {
  return {
    list: async ({ clusterId, scopeId, searchType = 'online' }) => {
      const qs = new URLSearchParams({ scope_id: scopeId, search_type: searchType })
      const res = await client.request(
        'GET',
        `/${clusterId}/mgmt/core/stream-management/?${qs.toString()}`,
        { schema: streamsResponse },
      )
      return { streams: res.data.result, total: res.count }
    },

    coverage: async ({ clusterId, scopeId }) => {
      const res = await client.request(
        'POST',
        `/${clusterId}/wrk/api/stream/analytics`,
        { body: { scope_id: scopeId }, schema: streamAnalyticsResponse },
      )
      // Drop the '*' wildcard aggregate; it is not a stream.
      const out: Record<string, StreamCoverage> = {}
      for (const [name, v] of Object.entries(res.result)) if (name !== '*') out[name] = v
      return out
    },

    dailyEventCount: async ({ clusterId, scopeId }) => {
      const res = await client.request(
        'POST',
        `/${clusterId}/mgmt/core/daily-stream-details`,
        { body: { scope_id: scopeId }, schema: dailyStreamResponse },
      )
      const out: Record<string, number> = {}
      for (const row of res.data) out[row.stream] = row.evt_len
      return out
    },
  }
}

/* ------------------------------------------------------------------ helpers */

/** Total workbooks attached to a stream. */
export function workbookCount(s: Stream): number {
  return s.running_workbooks.total_workbook_count ?? 0
}

const UNITS = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']

/** Bytes as a compact human string. Base 1024, since this is size on disk. */
export function formatBytes(bytes: number | null | undefined, digits = 1): string {
  if (typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes < 0) return '—'
  if (bytes === 0) return '0 B'
  const i = Math.min(UNITS.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)))
  const value = bytes / 1024 ** i
  // Whole bytes never need a decimal point.
  return `${i === 0 ? value : value.toFixed(digits)} ${UNITS[i]}`
}

/**
 * Fraction of logical size saved on disk, or null when the figure would not
 * mean anything for this stream.
 *
 * In the reference tenant 36 of 53 streams have size_on_disk LARGER than
 * total_len — by up to 61x — and it tracks stream size, with the smallest
 * streams worst (CONSOLE: 1.4 MB on disk for 275 KB logical). That is fixed
 * per-stream overhead dominating, so a ratio there measures overhead rather
 * than compression. Returning null lets the UI say so instead of rendering
 * a nonsense negative percentage.
 */
export function compressionSaved(s: Stream): number | null {
  const disk = s.size_on_disk
  const total = s.total_len
  if (!Number.isFinite(disk) || !Number.isFinite(total) || total <= 0) return null
  if (disk >= total) return null
  return 1 - disk / total
}

/**
 * Fleet-wide saving across the given streams.
 *
 * Sound even though many individual figures are not: the fixed overhead that
 * distorts small streams is negligible against the fleet totals (8.7 TB on
 * disk against 43.2 TB logical in the reference tenant, ~80% saved).
 */
export function fleetCompressionSaved(streams: Stream[]): number | null {
  let disk = 0
  let total = 0
  for (const s of streams) {
    disk += s.size_on_disk
    total += s.total_len
  }
  if (total <= 0 || disk >= total) return null
  return 1 - disk / total
}

/** 0.916 -> "91.6%". Dash when the ratio is not meaningful. */
export function formatPercent(v: number | null | undefined, digits = 1): string {
  return typeof v === 'number' && Number.isFinite(v) ? `${(v * 100).toFixed(digits)}%` : '—'
}

/** Thousands separators, for genuine counts (workbooks, rows) rather than sizes. */
export function formatCount(n: number | null | undefined): string {
  return typeof n === 'number' && Number.isFinite(n) ? n.toLocaleString('en-US') : '—'
}

const COUNT_UNITS: Array<[number, string]> = [
  [1e12, 'T'], [1e9, 'B'], [1e6, 'M'], [1e3, 'K'],
]

/**
 * Compact counts for table cells: 1125024794 -> "1.13B".
 *
 * Deliberately separate from formatBytes: these are event counts, and running
 * them through a byte formatter is what produced "922.7 MB" for 922.7 million
 * events.
 */
export function formatCompactCount(n: number | null | undefined, digits = 2): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—'
  const abs = Math.abs(n)
  for (const [size, suffix] of COUNT_UNITS) {
    if (abs >= size) return `${(n / size).toFixed(digits)}${suffix}`
  }
  return String(n)
}
